import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  CmsActions,
  CmsPageConnector,
  CmsStructureModel,
  LoggerService,
  tryNormalizeHttpError,
  PageContext,
  PageType,
  RoutingService,
  SemanticPathService,
  WindowRef
} from '@spartacus/core';
import { groupBy, mergeMap, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';

import { serializePageContext } from '@app/core/utils/cms.utils';

import { SITE_CONTEXT_ACTIONS } from '../site-context-actions.type';

@Injectable()
export class DpChangeCountryEffect {
  changeCountryEffect$;

  readonly #winRef = inject(WindowRef);
  readonly #actions$ = inject(Actions);
  readonly #routingService = inject(RoutingService);
  readonly #cmsPageConnector = inject(CmsPageConnector);
  readonly #logger = inject(LoggerService);
  readonly #notFoundPageContext: PageContext = {
    type: PageType.CONTENT_PAGE,
    id: inject(SemanticPathService).get('notFound') ?? '/not-found'
  };

  constructor() {
    this.changeCountryEffect$ = createEffect(() =>
      this.#actions$.pipe(
        ofType(SITE_CONTEXT_ACTIONS.CHANGE_COUNTRY),
        switchMap(() =>
          this.#getPageContext().pipe(
            groupBy((pageContext) => serializePageContext(pageContext)),
            mergeMap((group) =>
              group.pipe(
                switchMap((pageContext) =>
                  this.#cmsPageConnector.get(pageContext).pipe(
                    mergeMap((cmsStructure: CmsStructureModel) => this.#mapActions(pageContext, cmsStructure)),
                    catchError((error: HttpErrorResponse) => {
                      if (error.status === HttpStatusCode.NotFound) {
                        this.#winRef?.location?.reload?.();
                      }
                      return of(
                        new CmsActions.LoadCmsPageDataFail(pageContext, tryNormalizeHttpError(error, this.#logger))
                      );
                    })
                  )
                )
              )
            )
          )
        )
      )
    );
  }

  #getPageContext(): Observable<PageContext> {
    return this.#routingService.getRouterState().pipe(
      filter((routerState) => routerState?.state?.cmsRequired && !routerState.nextState),
      take(1),
      map((routerState) => routerState.state.context),
      tap((pageContext: PageContext) => {
        if (pageContext?.id === this.#notFoundPageContext.id) {
          this.#winRef?.location?.reload?.();
        }
      })
    );
  }

  #mapActions(pageContext: PageContext, cmsStructure: CmsStructureModel): Action[] {
    const actions: Action[] = [
      new CmsActions.CmsGetComponentFromPage(
        (cmsStructure.components ?? []).map((component) => ({ component, pageContext }))
      ),
      new CmsActions.LoadCmsPageDataSuccess(pageContext, cmsStructure.page ?? {})
    ];

    const pageLabel = cmsStructure.page?.label;
    if (pageLabel && pageLabel !== pageContext.id) {
      actions.unshift(
        new CmsActions.CmsSetPageSuccessIndex({ id: pageLabel, type: pageContext.type }, cmsStructure.page ?? {})
      );
    }

    return actions;
  }
}
